import { useEffect, useMemo, useRef, useState } from 'react';
import type { Car } from '../domain/types.ts';
import { groupOf } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import { PRINTED_LIVERY, asPrinted, liveryForDriver } from '../domain/livery.ts';
import { logoForTeam, useLogos } from '../lib/logoStore.ts';
import { CARD_H, CARD_W } from './boardModel.ts';
import { DriverCardFace, LIVERY_PREF_KEY } from './RaceBoards.tsx';
import type { TableRoll } from '../presentation/raceDice.ts';

/** The cars as they stood going into the turn on the table, for its driver cards. */
export interface TableField { cars: readonly Car[]; teams?: readonly Team[] }

/**
 * Which cards the table shows for a throw, and under what heading (7 Oct 2026).
 * Before the dice land: only the drivers the throw is FOR (known in advance —
 * nothing is given away). Once read: everyone the rule named, or, for a
 * selection, the one the face drew.
 */
export function tableCarsFor(roll: TableRoll, read: boolean): { ids: string[]; label: string; litId?: string } {
  if (!read) return { ids: roll.subjectIds, label: roll.selection !== undefined ? 'In the draw' : 'On this roll' };
  if (roll.pickedId) return { ids: [roll.pickedId], label: 'Drawn', litId: roll.pickedId };
  return { ids: roll.involvedIds, label: 'Involved' };
}

/** Everyone a run of throws turned out to be about, in the order they came up. */
export function involvedAcross(rolls: readonly TableRoll[]): string[] {
  return [...new Set(rolls.flatMap((r) => (r.pickedId ? [r.pickedId] : r.involvedIds)))];
}

/**
 * THE CARS ON THE DICE TABLE.
 *
 * Howard, 7 Oct 2026: "What if in the right section under the dice, we use the
 * actual driver cards when they are called? … if a die roll equals one or two or
 * even 3 or 4 cars their cards are duplicated, even resized if need be, so that
 * the user knows who is involved as the rolls are coming in that affects them."
 *
 * A COPY of the board's own card face (same paint, same hood mark, same chips),
 * scaled to the room the panel has. One to four drivers are drawn as cards; a
 * whole group (a pit reorder, a fuming driver's pool) is too many to read as
 * cards, so it prints as a row of car numbers instead.
 *
 * It only ever READS: the cars as they stood when the throw was made, and the
 * ids the roll's own record names. It never decides who is shown.
 */

/** More than this many and the cards would be too small to read. */
export const MAX_TABLE_CARDS = 4;
/** Never blow a card up past this — the print gets soft and it crowds the dice. */
const MAX_SCALE = 1.3;
const MIN_SCALE = 0.62;
const GAP = 10;

export interface TableCarsProps {
  ids: readonly string[];
  /** Every car in the race as it stood before this turn: running and retired. */
  cars: readonly Car[];
  teams?: readonly Team[];
  /** The heading over the cards: "On this roll", "Involved", "Drawn". */
  label: string;
  /** A driver to light up (the one a selection picked). */
  litId?: string;
}

/** "Dale Earnhardt Jr." → "Earnhardt Jr."; "Kyle Larson" → "Larson". */
export function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  const last = parts[parts.length - 1];
  return /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(last) && parts.length > 2 ? `${parts[parts.length - 2]} ${last}` : last;
}

function placeOf(car: Car): string {
  if (car.status !== 'running') return 'out';
  const group = groupOf(car.position);
  return `P${car.position} · ${group === 'leader' ? 'leader' : group}`;
}

export default function TableCars({ ids, cars, teams, label, litId }: TableCarsProps) {
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const logos = useLogos();
  const [printed, setPrinted] = useState(false);
  useEffect(() => {
    try {
      setPrinted(window.localStorage.getItem(LIVERY_PREF_KEY) === 'printed');
    } catch {
      /* the default stands */
    }
  }, []);

  const byId = useMemo(() => new Map(cars.map((c) => [c.card.driverId, c])), [cars]);
  const roster = useMemo(() => {
    const map = new Map<string, { team: Team; index: number }>();
    for (const team of teams ?? []) team.driverIds.forEach((id, index) => map.set(id, { team, index }));
    return map;
  }, [teams]);
  const shown = useMemo(() => ids.map((id) => byId.get(id)).filter((c): c is Car => !!c), [ids, byId]);

  const anyone = shown.length > 0;
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    // The room INSIDE the padding — clientWidth counts the padding too.
    const fit = () => {
      const css = window.getComputedStyle(node);
      setWidth(node.clientWidth - parseFloat(css.paddingLeft || '0') - parseFloat(css.paddingRight || '0'));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(node);
    return () => ro.disconnect();
  }, [anyone]);

  if (shown.length === 0) return null;
  const asCards = shown.length <= MAX_TABLE_CARDS;
  const n = shown.length;
  const room = width > 0 ? (width - GAP * (n - 1)) / (n * CARD_W) : 1;
  const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, room));
  const said = shown.map((c) => `#${c.card.carNumber} ${c.card.name}, ${placeOf(c)}`).join('; ');

  return (
    <div className="tablecars" ref={host}>
      <p className="tablecars__label">
        {label}
        {!asCards && <span className="tablecars__count"> · {n} cars</span>}
      </p>
      <p className="sr-only">
        {label}: {said}
      </p>
      {asCards ? (
        <div className="tablecars__row" aria-hidden="true">
          {shown.map((car) => {
            const seat = roster.get(car.card.driverId);
            const painted = liveryForDriver(car.card.driverId, car.card.teamId, seat?.index, seat?.team.baseInk, car.card.make) ?? PRINTED_LIVERY;
            const lit = car.card.driverId === litId;
            return (
              <figure key={car.card.driverId} className={`tablecar${lit ? ' is-lit' : ''}`}>
                <div className="tablecar__frame" style={{ width: CARD_W * scale, height: CARD_H * scale }}>
                  <div className="tablecar__scale" style={{ transform: `scale(${scale})` }}>
                    <DriverCardFace
                      car={car}
                      standalone
                      livery={printed ? asPrinted(painted) : painted}
                      printed={printed}
                      hoodLogo={logoForTeam(logos, seat?.team.name ?? null)}
                      highlighted={lit}
                      out={car.status !== 'running'}
                    />
                  </div>
                </div>
                <figcaption className="tablecar__place">{placeOf(car)}</figcaption>
              </figure>
            );
          })}
        </div>
      ) : (
        <ul className="tablecars__chips" aria-hidden="true">
          {shown.map((car) => (
            <li key={car.card.driverId} className={`tablechip${car.card.driverId === litId ? ' is-lit' : ''}`}>
              <b>#{car.card.carNumber}</b> {surname(car.card.name)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
